import React, { useState, useEffect } from 'react';
import { X, Wallet, Banknote, Calendar, FileText, AlertCircle, CheckCircle, Euro } from 'lucide-react';
import { createCashHandover } from '../../services/api';
import { toast } from 'sonner';

const CashHandoverModal = ({ isOpen, onClose, driver, onSuccess }) => {
  const [formData, setFormData] = useState({
    amount_lek: 0,
    amount_eur: 0,
    rides_count: 0,
    reason: 'Dorëzimi i xhiros ditore'
  });
  const [loading, setLoading] = useState(false);
  const [useLek, setUseLek] = useState(true); // Default to LEK
  const exchangeRate = 100; // 1 EUR = 100 LEK

  useEffect(() => {
    if (driver) {
      // wallet_balance is in EUR, convert to LEK for default display
      const balanceLek = (driver.wallet_balance || 0) * exchangeRate;
      setFormData(prev => ({
        ...prev,
        amount_lek: balanceLek,
        amount_eur: driver.wallet_balance || 0
      }));
    }
  }, [driver]);

  const handleAmountChange = (value, isLek = true) => {
    const numValue = parseFloat(value) || 0;
    if (isLek) {
      setFormData(prev => ({
        ...prev,
        amount_lek: numValue,
        amount_eur: numValue / exchangeRate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        amount_eur: numValue,
        amount_lek: numValue * exchangeRate
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!driver || formData.amount_lek <= 0) {
      toast.error('Ju lutem vendosni një shumë të vlefshme');
      return;
    }

    setLoading(true);
    try {
      await createCashHandover({
        driver_id: driver.id,
        amount: formData.amount_eur, // Backend expects EUR
        amount_lek: formData.amount_lek,
        rides_count: formData.rides_count,
        reason: formData.reason
      });
      
      toast.success(`Xhiroja u dorëzua me sukses: ${formData.amount_lek.toLocaleString()} L`);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error creating cash handover:', error);
      toast.error(error.response?.data?.detail || 'Gabim gjatë dorëzimit të xhiros');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !driver) return null;

  const balanceLek = (driver.wallet_balance || 0) * exchangeRate;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Dorëzo Xhiron</h2>
                <p className="text-sm text-gray-500">{driver.name}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Current Balance Info */}
        <div className="p-4 mx-6 mt-4 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm text-amber-800 font-medium">Bilanci aktual në portofol</p>
              <p className="text-2xl font-bold text-amber-900">
                {balanceLek.toLocaleString()} L
                <span className="text-sm font-normal text-amber-700 ml-2">
                  (≈ {(driver.wallet_balance || 0).toFixed(2)} €)
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Currency Toggle */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => setUseLek(true)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                useLek ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Banknote className="w-4 h-4" />
              LEK (L)
            </button>
            <button
              type="button"
              onClick={() => setUseLek(false)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                !useLek ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Euro className="w-4 h-4" />
              EUR (€)
            </button>
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shuma për dorëzim
            </label>
            <div className="relative">
              {useLek ? (
                <Banknote className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              ) : (
                <Euro className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              )}
              <input
                type="number"
                step={useLek ? "100" : "0.01"}
                min="0"
                value={useLek ? formData.amount_lek : formData.amount_eur}
                onChange={(e) => handleAmountChange(e.target.value, useLek)}
                className="w-full pl-12 pr-16 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg font-semibold"
                placeholder={useLek ? "0" : "0.00"}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                {useLek ? 'L' : '€'}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {useLek 
                ? `≈ ${formData.amount_eur.toFixed(2)} €` 
                : `= ${formData.amount_lek.toLocaleString()} L`
              }
            </p>
          </div>

          {/* Quick Amount Buttons - in LEK */}
          <div className="flex gap-2">
            {[1000, 2000, 5000, 10000].map((amount) => (
              <button
                key={amount}
                type="button"
                onClick={() => handleAmountChange(amount, true)}
                className="flex-1 py-2 px-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                {(amount / 1000).toFixed(0)}k L
              </button>
            ))}
            <button
              type="button"
              onClick={() => handleAmountChange(balanceLek, true)}
              className="flex-1 py-2 px-3 rounded-lg border border-green-200 bg-green-50 text-sm font-medium text-green-700 hover:bg-green-100 transition-colors"
            >
              Gjithë
            </button>
          </div>

          {/* Rides Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Numri i udhëtimeve (opsional)
            </label>
            <input
              type="number"
              min="0"
              value={formData.rides_count}
              onChange={(e) => setFormData(prev => ({ ...prev, rides_count: parseInt(e.target.value) || 0 }))}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="0"
            />
          </div>

          {/* Reason/Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Arsyeja / Shënime
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              rows={2}
              placeholder="Dorëzimi i xhiros ditore..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || formData.amount_lek <= 0}
            className="w-full py-3.5 rounded-xl text-white font-semibold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-500/25"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Duke procesuar...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Konfirmo Dorëzimin ({formData.amount_lek.toLocaleString()} L)
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CashHandoverModal;
